import PptxGenJS from "pptxgenjs";
import { TEXT_OPTIONS } from "../masters/components";
import { filter, flatMap, includes, orderBy, pick, reduce } from "lodash";
import { DimensionWithScores } from "@/features/people";

// Common constants
export const CHART_CONSTANTS = {
  RIGHT_COLUMN_X: 8.99,
  LEFT_COLUMN_X: 0.52,
  COLUMN_Y: 2.0,
  LABEL_WIDTH: 1.5,
  CHART_WIDTH: 6.1,
  SECTION_LABELS_Y: 2.2,
  BARS_START_Y: 2.6,
  SECTION_LINE_ADJUSTMENT: 0.05,
};

// Chart configuration types
export interface ChartSection {
  label: string;
  x: number;
  width: number;
}

export interface ChartConfig {
  barHeight: number;
  barGap: number;
  verticalLinePositions: number[];
  sections: ChartSection[];
}

export interface SlideConfig {
  title: string;
  dataKey: string;
  scaleColor: string;
  scaleMaxValue: number;
  descriptionText: string;
  scaleDefinitions: Array<{ title: string; description: string }>;
  chartConfig: ChartConfig;
}

/**
 * Creates chart sections with equal widths
 */
export const createEqualSections = (
  labels: string[],
  chartStartX: number,
  chartWidth: number
): ChartSection[] => {
  const sectionWidth = chartWidth / labels.length;
  return labels.map((label, index) => ({
    label,
    x: chartStartX + index * sectionWidth,
    width: sectionWidth,
  }));
};

/**
 * Creates chart sections with custom widths (percentages that sum to 100)
 */
export const createCustomSections = (
  sections: Array<{ label: string; widthPercent: number }>,
  chartStartX: number,
  chartWidth: number
): ChartSection[] => {
  let currentX = chartStartX;
  return sections.map((section) => {
    const width = (chartWidth * section.widthPercent) / 100;
    const chartSection: ChartSection = {
      label: section.label,
      x: currentX,
      width,
    };
    currentX += width;
    return chartSection;
  });
};

/**
 * Adds section labels with arrow lines
 */
export const addSectionLabels = (
  slide: PptxGenJS.Slide,
  sections: ChartSection[]
) => {
  const { SECTION_LABELS_Y, SECTION_LINE_ADJUSTMENT } = CHART_CONSTANTS;

  sections.forEach((section) => {
    // Add horizontal line with arrows
    slide.addShape("line", {
      x: section.x + SECTION_LINE_ADJUSTMENT,
      y: SECTION_LABELS_Y,
      w: section.width - SECTION_LINE_ADJUSTMENT * 2,
      h: 0,
      line: {
        color: "CCCCCC",
        width: 1,
        beginArrowType: "arrow",
        endArrowType: "arrow",
      },
    });

    // Add section label
    slide.addText(section.label, {
      x: section.x,
      y: SECTION_LABELS_Y - 0.3,
      w: section.width,
      h: 0.2,
      fontSize: 10,
      color: "999999",
      align: "center",
      valign: "middle",
    });
  });
};

/**
 * Adds vertical dotted lines at specified percentages
 */
export const addVerticalLines = (
  slide: PptxGenJS.Slide,
  chartStartX: number,
  chartWidth: number,
  verticalLinePositions: number[],
  linesHeight: number
) => {
  const { BARS_START_Y } = CHART_CONSTANTS;

  verticalLinePositions.forEach((percentage) => {
    const lineX = chartStartX + (chartWidth * percentage) / 100;
    slide.addShape("line", {
      x: lineX,
      y: BARS_START_Y - 0.3,
      w: 0,
      h: linesHeight,
      line: {
        color: "CCCCCC",
        width: 1,
        dashType: "dash",
      },
    });
  });
};

/**
 * Adds horizontal bars for each data item
 */
export const addHorizontalBars = (
  slide: PptxGenJS.Slide,
  data: Array<{ title: string; score: { value: number } }>,
  chartStartX: number,
  chartWidth: number,
  barHeight: number,
  barGap: number,
  barColor: string,
  scaleMaxValue: number
) => {
  const { LABEL_WIDTH, BARS_START_Y } = CHART_CONSTANTS;

  data.forEach((item, index) => {
    const y = BARS_START_Y + index * (barHeight + barGap);
    const value = item.score.value;
    const barWidth = (value / scaleMaxValue) * chartWidth;

    // Add label on the left
    slide.addText(item.title, {
      x: 0,
      y: y,
      w: LABEL_WIDTH - 0.1,
      h: barHeight,
      fontSize: 11,
      color: "000000",
      align: "right",
      valign: "middle",
    });

    // Add colored bar
    slide.addShape("rect", {
      x: chartStartX,
      y: y,
      w: barWidth,
      h: barHeight,
      fill: { color: barColor },
      line: { type: "none" },
    });

    // Add value text on the bar (white color)
    slide.addText(value.toString(), {
      x: chartStartX + barWidth - 1.55,
      y: y,
      w: 1.5,
      h: barHeight,
      fontSize: 14,
      color: "FFFFFF",
      bold: true,
      align: "right",
    });
  });
};

/**
 * Adds the right column content (line, description, and scale definitions)
 */
export const addRightColumnContent = (
  slide: PptxGenJS.Slide,
  scaleColor: string,
  descriptionText: string,
  scaleDefinitions: Array<{ title: string; description: string }>
) => {
  const { RIGHT_COLUMN_X, COLUMN_Y } = CHART_CONSTANTS;

  const columnsBaseOptions = {
    y: COLUMN_Y,
    w: 6.81,
    h: 5.2,
    valign: "top",
  } as const;

  // Add colored line at top right
  slide.addShape("line", {
    x: 9.09,
    y: 0.56,
    w: 6.71,
    h: 0,
    line: {
      color: scaleColor,
      width: 4.5,
    },
  });

  // Add description text
  slide.addText(
    [
      {
        text: descriptionText,
        options: { ...TEXT_OPTIONS.body },
      },
    ],
    {
      ...columnsBaseOptions,
      y: 0.7,
      x: RIGHT_COLUMN_X,
    }
  );

  // Add scale definitions
  slide.addText(
    [
      {
        text: "SCALE DEFINITIONS",
        options: {
          ...TEXT_OPTIONS.caption2,
          align: "left",
          paraSpaceAfter: 10,
          fontSize: 10,
          bold: false,
        },
      },
      ...flatMap(scaleDefinitions, (item) => [
        {
          text: item.title,
          options: { ...TEXT_OPTIONS.caption2, align: "left" } as const,
        },
        {
          text: item.description,
          options: {
            ...TEXT_OPTIONS.body,
            align: "left",
            paraSpaceAfter: 8,
          } as const,
        },
      ]),
    ],
    {
      ...columnsBaseOptions,
      y: 1.7,
      x: RIGHT_COLUMN_X,
    }
  );
};

const OPQ_DATA_KEYS = {
  CONSISTENCY: "opq_consistency",
  FEELINGS_AND_EMOTIONS: "opq_feelings_and_emotions",
  RELATIONSHIPS_WITH_PEOPLE: "opq_relationships_with_people",
  THINKING_STYLE: "opq_thinking_style",
};

// Innovative
// Optimistic
// Evaluative
// Socially Confident
// Forward Thinking
// Affiliative
// Conventional
// Rule Following

const OPQ_CHART_DATA_KEYS = [
  "innovative",
  "optimistic",
  "evaluative",
  "socially_confident",
  "forward_thinking",
  "affiliative",
  "conventional",
  "rule_following",
];

const selectOpqChartData = (data: any) => {
  const opqDataObject = pick(data.dimensionsData, Object.values(OPQ_DATA_KEYS)); // object with keys as OPQ_DATA_KEYS and values as DimensionWithScores[]
  const opqData = reduce(
    opqDataObject,
    (acc, dimensionsData) => {
      const items = filter(dimensionsData, (item) =>
        includes(OPQ_CHART_DATA_KEYS, item.code)
      );
      return [...acc, ...items];
    },
    [] as DimensionWithScores[]
  );
  return orderBy(opqData, (item) => item.score.value, "asc");
};

const selectChartData = (data: any, dataKey: string) => {
  if (dataKey === "opq") {
    return selectOpqChartData(data);
  }

  return data.dimensionsData?.[dataKey];
};

/**
 * Main function to render a complete scores chart slide
 */
export const renderScoresChartSlide = (
  pres: PptxGenJS,
  slide: PptxGenJS.Slide,
  data: any,
  candidateName: string | undefined,
  config: SlideConfig
) => {
  const { LEFT_COLUMN_X, LABEL_WIDTH, CHART_WIDTH } = CHART_CONSTANTS;
  const {
    chartConfig,
    scaleColor,
    descriptionText,
    scaleDefinitions,
    dataKey,
    scaleMaxValue,
  } = config;

  // Add title (shrinkText ensures long titles scale to fit)
  slide.addText(config.title, { placeholder: "title", shrinkText: true });

  // Add candidate name
  if (candidateName) {
    slide.addText(candidateName, {
      ...TEXT_OPTIONS.caption,
      x: LEFT_COLUMN_X,
      y: 1.14,
      fontSize: 18,
    });
  }

  // Get chart data
  const chartData = selectChartData(data, dataKey);

  if (chartData && chartData.length > 0) {
    const chartStartX = LABEL_WIDTH;

    // Add section labels with arrows
    addSectionLabels(slide, chartConfig.sections);

    // Add vertical dotted lines
    const linesHeight =
      chartData.length * (chartConfig.barHeight + chartConfig.barGap) + 0.3;
    addVerticalLines(
      slide,
      chartStartX,
      CHART_WIDTH,
      chartConfig.verticalLinePositions,
      linesHeight
    );

    // Add horizontal bars
    addHorizontalBars(
      slide,
      chartData,
      chartStartX,
      CHART_WIDTH,
      chartConfig.barHeight,
      chartConfig.barGap,
      scaleColor,
      scaleMaxValue
    );
  }

  // Add right column content
  addRightColumnContent(slide, scaleColor, descriptionText, scaleDefinitions);
};
